import { motion } from 'framer-motion';
import { MapPin, Clock, DollarSign, ArrowRight, Heart, Zap, Globe, Users } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const positions = [
  {
    id: 'senior-ml-engineer',
    title: 'Senior ML Engineer',
    department: 'Engineering',
    location: 'Remote (US/EU)',
    type: 'Full-time',
    salary: '$180k - $250k',
  },
  {
    id: 'full-stack-developer',
    title: 'Full Stack Developer',
    department: 'Engineering',
    location: 'Remote (Global)',
    type: 'Full-time',
    salary: '$140k - $200k',
  },
  {
    id: 'product-designer',
    title: 'Product Designer',
    department: 'Design',
    location: 'Remote (US/EU)',
    type: 'Full-time',
    salary: '$120k - $170k',
  },
  {
    id: 'devrel-engineer',
    title: 'Developer Relations Engineer',
    department: 'Marketing',
    location: 'Remote (Global)',
    type: 'Full-time',
    salary: '$130k - $180k',
  },
  {
    id: 'customer-success-manager',
    title: 'Customer Success Manager',
    department: 'Customer Success',
    location: 'Remote (US)',
    type: 'Full-time',
    salary: '$90k - $130k',
  },
  {
    id: 'content-writer',
    title: 'Technical Content Writer',
    department: 'Marketing',
    location: 'Remote (Global)',
    type: 'Full-time',
    salary: '$80k - $120k',
  },
];

const benefits = [
  {
    icon: Globe,
    title: 'Remote First',
    description: 'Work from anywhere in the world. We believe talent is global.',
  },
  {
    icon: DollarSign,
    title: 'Competitive Pay',
    description: 'Top-tier salaries plus equity. We pay at the 90th percentile.',
  },
  {
    icon: Heart,
    title: 'Health & Wellness',
    description: 'Premium health, dental, and vision coverage for you and family.',
  },
  {
    icon: Clock,
    title: 'Flexible Hours',
    description: 'Set your own schedule. We care about results, not hours logged.',
  },
  {
    icon: Zap,
    title: 'Learning Budget',
    description: '$3,000/year for courses, conferences, and professional development.',
  },
  {
    icon: Users,
    title: 'Team Retreats',
    description: 'Annual company retreats to connect and collaborate in person.',
  },
];

const values = [
  {
    emoji: '🚀',
    title: 'Move Fast',
    description: "We ship quickly and iterate based on feedback. Perfect is the enemy of good.",
  },
  {
    emoji: '🤝',
    title: 'Customer Obsessed',
    description: 'Every decision starts with "How does this help our customers?"',
  },
  {
    emoji: '💡',
    title: 'Think Big',
    description: "We're building the future of work. Bold ideas are encouraged.",
  },
  {
    emoji: '🌟',
    title: 'Own Your Work',
    description: 'Take initiative. Make decisions. Be accountable for outcomes.',
  },
];

export default function Careers() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title="Join Our Team"
      subtitle="Help us build the future of work with AI employees."
    >
      {/* Mission Banner */}
      <section className="mb-16">
        <div className={`p-8 rounded-2xl border text-center ${
          isDark
            ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
            : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
        }`}>
          <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            We're on a mission to give every business superpowers
          </h2>
          <p className={`max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
            Join a team of passionate builders, thinkers, and dreamers who believe that AI should
            be accessible to everyone. We're growing fast and looking for exceptional people.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>Our Values</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-6 rounded-2xl border text-center ${
                isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              <span className="text-4xl mb-4 block">{value.emoji}</span>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{value.title}</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{value.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>Benefits & Perks</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`flex items-start gap-4 p-6 rounded-2xl border ${
                isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center shrink-0">
                <benefit.icon className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
              <div>
                <h3 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{benefit.title}</h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{benefit.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Open Positions */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>Open Positions</h2>
        <div className="space-y-4">
          {positions.map((position, index) => (
            <motion.div
              key={position.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`group flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 rounded-2xl border transition-all ${
                isDark
                  ? 'bg-dark-800/50 border-white/5 hover:border-purple-500/30'
                  : 'bg-white border-slate-200 hover:border-purple-300 shadow-sm'
              }`}
            >
              <div>
                <span className={`text-sm mb-1 block ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>{position.department}</span>
                <h3 className={`text-xl font-semibold transition-colors ${
                  isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
                }`}>
                  {position.title}
                </h3>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <span className={`flex items-center gap-1 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                  <MapPin className="w-4 h-4" />
                  {position.location}
                </span>
                <span className={`flex items-center gap-1 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                  <Clock className="w-4 h-4" />
                  {position.type}
                </span>
                <span className={`flex items-center gap-1 text-sm ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                  <DollarSign className="w-4 h-4" />
                  {position.salary}
                </span>
                <button className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                  isDark
                    ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                    : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                }`}>
                  Apply <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className={`text-center p-8 rounded-2xl border ${
        isDark
          ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
          : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
      }`}>
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Don't See a Good Fit?</h2>
        <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          We're always looking for talented people. Send us your resume and tell us how you can
          contribute.
        </p>
        <a
          href="mailto:careers@flowai.com"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
        >
          Send Your Resume
        </a>
      </section>
    </StaticPageLayout>
  );
}
